import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, RdsInstance, RdsInstanceEvent, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'instances' | 'activity';

const DEFAULT_SORT = 'id-asc';
const FACET_KEYS   = ['engine', 'status', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'id-asc',          label: 'Identifier A → Z'  },
  { value: 'id-desc',         label: 'Identifier Z → A'  },
  { value: 'engine-asc',      label: 'Engine'             },
  { value: 'monitored-first', label: 'Monitored first'    },
];

const FACETS: FacetDef[] = [
  {
    key: 'engine',
    label: 'Engine',
    options: [
      { value: 'mysql',            label: 'MySQL'       },
      { value: 'postgres',         label: 'PostgreSQL'  },
      { value: 'mariadb',          label: 'MariaDB'     },
      { value: 'oracle-ee',        label: 'Oracle'      },
      { value: 'sqlserver-ex',     label: 'SQL Server'  },
      { value: 'aurora-mysql',     label: 'Aurora MySQL'},
      { value: 'aurora-postgresql',label: 'Aurora PG'  },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'available', label: 'Available' },
      { value: 'stopped',   label: 'Stopped'   },
      { value: 'failed',    label: 'Failed'     },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

@Component({
  selector: 'app-rds-detail',
  templateUrl: './rds-detail.component.html',
})
export class RdsDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  rdsInstances: RdsInstance[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  selectedDb: string | null = null;
  dbEvents: RdsInstanceEvent[] = [];
  dbEventsLoading = false;
  dbEventsError: string | null = null;
  dbPage = 1;
  readonly dbPageSize = 10;

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',  label: 'Overview'  },
    { id: 'instances', label: 'Instances' },
    { id: 'activity',  label: 'Activity'  },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'rds', name: 'RDS', icon: 'database', route: '/cloud/rds', status: 'healthy', metrics: [] };

  drawerItemId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private dbSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('rds').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      this.filters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      const db = (params as Record<string, string>)['db'] ?? null;
      if (db !== this.selectedDb) {
        this.selectedDb = db;
        if (db) { this.dbPage = 1; this.loadDbEvents(db); }
        else { this.dbSub?.unsubscribe(); this.dbEvents = []; }
      }
    });
    this.loadData(false);
  }

  /** Loads the instance list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getRdsInstances(bypass).subscribe(instances => {
      this.rdsInstances = instances;
      this.loading      = false;
      this.refreshing   = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'RDS_INSTANCE'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.dbSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'instances';
    this.selectDb(item.resourceId);
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.rdsInstances.length; }
  get monitoredCount(): number { return this.rdsInstances.filter(i => this.cloud.isMonitored('rds', i.dbInstanceIdentifier)).length; }

  // ── Overview counts (unfiltered) ────────────────────────────────────────────

  get availableCount(): number { return this.rdsInstances.filter(i => i.status === 'available').length; }
  get stoppedCount():   number { return this.rdsInstances.filter(i => i.status === 'stopped' || i.status === 'stopping' || i.status === 'starting').length; }
  get failedCount():    number { return this.rdsInstances.filter(i => i.status === 'failed' || i.status.startsWith('incompatible')).length; }
  get multiAZCount():   number { return this.rdsInstances.filter(i => i.multiAZ).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredInstances(): RdsInstance[] {
    let result = this.rdsInstances;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(i =>
      i.dbInstanceIdentifier.toLowerCase().includes(q) ||
      i.engine.toLowerCase().includes(q) ||
      i.dbInstanceClass.toLowerCase().includes(q),
    );

    const engines = this.filters.facets['engine'] ?? [];
    if (engines.length) result = result.filter(i => engines.includes(i.engine));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(i => statuses.includes(i.status));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(i => {
        const mon = this.cloud.isMonitored('rds', i.dbInstanceIdentifier);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(instances: RdsInstance[], sort: string): RdsInstance[] {
    return [...instances].sort((a, b) => {
      switch (sort) {
        case 'id-desc':         return b.dbInstanceIdentifier.localeCompare(a.dbInstanceIdentifier);
        case 'engine-asc':      return a.engine.localeCompare(b.engine) || a.dbInstanceIdentifier.localeCompare(b.dbInstanceIdentifier);
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('rds', a.dbInstanceIdentifier) ? 0 : 1;
          const bM = this.cloud.isMonitored('rds', b.dbInstanceIdentifier) ? 0 : 1;
          return aM - bM;
        }
        default: return a.dbInstanceIdentifier.localeCompare(b.dbInstanceIdentifier);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Drill-down ──────────────────────────────────────────────────────────────

  selectDb(dbInstanceIdentifier: string): void {
    this.dbPage = 1;
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { db: dbInstanceIdentifier },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closeDb(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { db: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadDbEvents(dbInstanceIdentifier: string): void {
    this.dbEventsLoading = true;
    this.dbEventsError   = null;
    this.dbSub?.unsubscribe();
    this.dbSub = this.cloud.getRdsInstanceEvents(dbInstanceIdentifier).subscribe({
      next:  evs => { this.dbEvents = evs; this.dbEventsLoading = false; },
      error: ()  => { this.dbEventsError = 'Failed to load instance events.'; this.dbEventsLoading = false; },
    });
  }

  get pagedDbEvents(): RdsInstanceEvent[] {
    const start = (this.dbPage - 1) * this.dbPageSize;
    return this.dbEvents.slice(start, start + this.dbPageSize);
  }

  get dbPageTotal(): number { return Math.ceil(this.dbEvents.length / this.dbPageSize); }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(dbInstanceIdentifier: string): boolean { return this.cloud.isMonitored('rds', dbInstanceIdentifier); }

  toggleMonitoring(dbInstanceIdentifier: string): void {
    const current = this.cloud.isMonitored('rds', dbInstanceIdentifier);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('rds', dbInstanceIdentifier, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(dbInstanceIdentifier: string): void  { this.drawerItemId = dbInstanceIdentifier; }
  closeItemSettings(): void                             { this.drawerItemId = null; }

  itemOverrideSummary(dbInstanceIdentifier: string): string {
    return this.cloud.getItemOverrideSummary('rds', dbInstanceIdentifier);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(status: string): string {
    if (status === 'available')                       return 'bg-emerald-100 text-emerald-700';
    if (status === 'stopped')                         return 'bg-slate-100 text-slate-500';
    if (status === 'failed' || status.startsWith('incompatible')) return 'bg-red-100 text-red-700';
    if (status === 'deleting' || status === 'deleted') return 'bg-slate-100 text-slate-400';
    return 'bg-amber-100 text-amber-700'; // transitional
  }
}
